<template>
  <div class="admin min-h-screen bg-gradient-to-br from-gray-900/70 via-black/50 to-gray-900/70">
    <!-- Indicateur de chargement global -->
    <div v-if="loading.global" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div class="bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl border-2 border-amber-700/40 p-8 relative box-glow">
        <div class="flex items-center space-x-4">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
          <span class="text-amber-300 text-lg">Chargement des données...</span>
        </div>
      </div>
    </div>
    
    <header class="bg-gradient-to-r from-amber-900/50 via-black/95 to-gray-900/90 backdrop-blur-sm shadow-xl border-b border-amber-600/30 py-6 px-4 sm:px-6 lg:px-8">
      <div class="max-w-7xl mx-auto">
        <h1 class="text-3xl font-bold text-amber-300 flex items-center">
          <svg class="w-8 h-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
          </svg>
          Centre d'Administration
        </h1>
      </div>
    </header>
    
    <main>
      <div class="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <!-- Menu de navigation d'administration -->
        <div class="bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/40 mb-8 relative box-glow">
          <div class="absolute inset-0 opacity-10 bg-amber-300 pattern-warhammer"></div>
          <nav class="flex relative">
            <button 
              v-for="(tab, index) in tabs" 
              :key="index" 
              @click="activeTab = tab.id"
              class="relative px-5 py-4 transition-colors duration-200 flex items-center font-medium"
              :class="activeTab === tab.id ? 'text-amber-300' : 'text-amber-300/60 hover:text-amber-300'"
            >
              <span class="absolute bottom-0 left-0 w-full h-0.5 bg-amber-500 origin-left transform scale-x-0 transition-transform duration-300" :class="{ 'scale-x-100': activeTab === tab.id }"></span>
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path :d="tab.icon" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
              </svg>
              <span>{{ tab.name }}</span>
            </button>
          </nav>
        </div>
        
        <!-- Contenu dynamique en fonction de l'onglet actif -->
        <div class="space-y-6">
          <!-- Tableau de bord -->
          <div v-if="activeTab === 'dashboard'" class="animate-fadeIn">
            <div class="mb-8 bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/40 p-6 relative box-glow">
              <div class="absolute inset-0 opacity-5 bg-amber-300 pattern-warhammer"></div>
              <div class="relative">
                <h2 class="text-xl font-bold text-amber-300 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                  </svg>
                  Aperçu du système
                </h2>
            
            <!-- Cards des statistiques -->
                <div v-if="loading.stats" class="flex justify-center items-center py-8">
                  <div class="text-amber-300">Chargement des statistiques...</div>
                </div>
                <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-4">
                  <div class="bg-black/60 rounded-lg p-5 border border-amber-700/30 shadow-inner">
                  <div class="flex items-center">
                      <div class="flex-shrink-0 bg-gradient-to-br from-amber-500 to-amber-700 rounded-lg p-3 shadow-lg">
                        <svg class="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
                        </svg>
                    </div>
                    <div class="ml-5 w-0 flex-1">
                      <dl>
                          <dt class="text-sm font-medium text-amber-200/60 truncate">Total utilisateurs</dt>
                          <dd>
                            <div class="flex items-baseline">
                              <div class="text-2xl font-bold text-amber-300">{{ stats.users }}</div>
                              <div v-if="stats.newUsers > 0" class="ml-2 text-sm text-green-400 flex items-center">
                                <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18"/>
                                </svg>
                                <span>+{{ stats.newUsers }}</span>
                              </div>
                            </div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
                  
                  <div class="bg-black/60 rounded-lg p-5 border border-amber-700/30 shadow-inner">
                    <div class="flex items-center">
                      <div class="flex-shrink-0 bg-gradient-to-br from-amber-500 to-amber-700 rounded-lg p-3 shadow-lg">
                        <svg class="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                        </svg>
                      </div>
                      <div class="ml-5 w-0 flex-1">
                        <dl>
                          <dt class="text-sm font-medium text-amber-200/60 truncate">Total matchs</dt>
                          <dd>
                            <div class="flex items-baseline">
                              <div class="text-2xl font-bold text-amber-300">{{ stats.matches }}</div>
                              <div v-if="stats.newMatches > 0" class="ml-2 text-sm text-green-400 flex items-center">
                                <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18"/>
                                </svg>
                                <span>+{{ stats.newMatches }}</span>
                              </div>
                            </div>
                          </dd>
                        </dl>
                  </div>
                </div>
              </div>
              
                  <div class="bg-black/60 rounded-lg p-5 border border-amber-700/30 shadow-inner">
                  <div class="flex items-center">
                      <div class="flex-shrink-0 bg-gradient-to-br from-amber-500 to-amber-700 rounded-lg p-3 shadow-lg">
                        <svg class="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                        </svg>
                    </div>
                    <div class="ml-5 w-0 flex-1">
                      <dl>
                          <dt class="text-sm font-medium text-amber-200/60 truncate">Joueurs actifs</dt>
                        <dd>
                            <div class="flex items-baseline">
                              <div class="text-2xl font-bold text-amber-300">{{ stats.activePlayers }}%</div>
                            </div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
                  
                  <div class="bg-black/60 rounded-lg p-5 border border-amber-700/30 shadow-inner">
                    <div class="flex items-center">
                      <div class="flex-shrink-0 bg-gradient-to-br from-amber-500 to-amber-700 rounded-lg p-3 shadow-lg">
                        <svg class="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                        </svg>
                      </div>
                      <div class="ml-5 w-0 flex-1">
                        <dl>
                          <dt class="text-sm font-medium text-amber-200/60 truncate">Matchs ce mois</dt>
                          <dd>
                            <div class="flex items-baseline">
                              <div class="text-2xl font-bold text-amber-300">{{ stats.monthlyMatches }}</div>
                            </div>
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                  </div>
                </div>
              </div>
              
            <!-- Activité récente -->
            <div class="mb-8 bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/40 relative box-glow">
              <div class="absolute inset-0 opacity-5 bg-amber-300 pattern-warhammer"></div>
              <div class="px-6 py-5 border-b border-amber-800/60 bg-gradient-to-r from-amber-900/60 via-black/90 to-amber-900/60 relative">
                <div class="absolute inset-0 opacity-10 bg-amber-300 pattern-warhammer"></div>
                <h2 class="text-xl font-bold text-amber-300 flex items-center relative">
                  <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  Activité récente
                </h2>
              </div>
              <div class="p-6 relative">
                <div class="flex items-center justify-between mb-4">
                  <div class="flex space-x-2">
                    <button @click="activitiesFilter = 'all'; activitiesPage = 1" :class="activitiesFilter === 'all' ? 'bg-amber-700/40 text-amber-200' : 'bg-black/40 text-amber-300/70'" class="px-3 py-1 rounded-full border border-amber-700/40 text-xs">Tous</button>
                    <button @click="activitiesFilter = 'match'; activitiesPage = 1" :class="activitiesFilter === 'match' ? 'bg-amber-700/40 text-amber-200' : 'bg-black/40 text-amber-300/70'" class="px-3 py-1 rounded-full border border-amber-700/40 text-xs">Matchs</button>
                    <button @click="activitiesFilter = 'user'; activitiesPage = 1" :class="activitiesFilter === 'user' ? 'bg-amber-700/40 text-amber-200' : 'bg-black/40 text-amber-300/70'" class="px-3 py-1 rounded-full border border-amber-700/40 text-xs">Inscriptions</button>
                    <button @click="activitiesFilter = 'moderation'; activitiesPage = 1" :class="activitiesFilter === 'moderation' ? 'bg-amber-700/40 text-amber-200' : 'bg-black/40 text-amber-300/70'" class="px-3 py-1 rounded-full border border-amber-700/40 text-xs">Modération</button>
                  </div>
                  <button @click="loadActivities" class="px-3 py-1 bg-gradient-to-r from-amber-600/80 to-amber-800/80 text-amber-100 rounded-lg hover:from-amber-600/90 hover:to-amber-800/90 transition-all duration-300 border border-amber-700/40 text-xs">Actualiser</button>
                </div>

                <div v-if="loadingActivities" class="py-6 text-center text-amber-300">Chargement des activités...</div>
                <ul v-else class="space-y-3">
                  <li v-for="(activity, index) in pagedActivities" :key="index" class="flex items-start p-3 rounded-lg" :class="index % 2 === 0 ? 'bg-black/40' : 'bg-amber-950/20'">
                    <div class="flex-shrink-0">
                      <div class="w-10 h-10 rounded-full flex items-center justify-center" :class="activityTypeClasses[activity.type]">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path :d="activityTypeIcons[activity.type]" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                        </svg>
                      </div>
                    </div>
                    <div class="ml-4 flex-1">
                      <div class="flex justify-between">
                        <p class="text-sm font-medium text-amber-200">{{ activity.title }}</p>
                        <p class="text-xs text-amber-200/60">{{ formatDate(activity.timestamp) }}</p>
                      </div>
                      <p class="text-sm text-amber-300/70 mt-1">{{ activity.message }}</p>
                    </div>
                  </li>
                  <li v-if="filteredActivities.length === 0" class="p-4 text-center text-amber-300/60">
                    Aucune activité récente à afficher
                  </li>
                </ul>

                <!-- Pagination -->
                <div v-if="filteredActivities.length > 0" class="mt-4 flex items-center justify-between text-xs text-amber-300/70">
                  <div>
                    Page {{ activitiesPage }} / {{ totalActivitiesPages }}
                  </div>
                  <div class="space-x-2">
                    <button @click="activitiesPage = 1" :disabled="activitiesPage === 1" class="px-2 py-1 rounded border border-amber-700/40 hover:bg-amber-700/20 disabled:opacity-40">«</button>
                    <button @click="activitiesPage = Math.max(1, activitiesPage - 1)" :disabled="activitiesPage === 1" class="px-2 py-1 rounded border border-amber-700/40 hover:bg-amber-700/20 disabled:opacity-40">Précédent</button>
                    <button @click="activitiesPage = Math.min(totalActivitiesPages, activitiesPage + 1)" :disabled="activitiesPage === totalActivitiesPages" class="px-2 py-1 rounded border border-amber-700/40 hover:bg-amber-700/20 disabled:opacity-40">Suivant</button>
                    <button @click="activitiesPage = totalActivitiesPages" :disabled="activitiesPage === totalActivitiesPages" class="px-2 py-1 rounded border border-amber-700/40 hover:bg-amber-700/20 disabled:opacity-40">»</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Gestion des utilisateurs -->
          <div v-if="activeTab === 'users'" class="animate-fadeIn">
            <div class="mb-8 bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/40 p-6 relative box-glow">
              <div class="absolute inset-0 opacity-5 bg-amber-300 pattern-warhammer"></div>
              <div class="relative">
                <div class="flex justify-between items-center mb-6">
                  <h2 class="text-xl font-bold text-amber-300 flex items-center">
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
                    </svg>
                    Gestion des utilisateurs
                  </h2>
                  <div class="relative">
                    <input 
                      type="text" 
                      v-model="searchUser" 
                      placeholder="Rechercher un utilisateur..." 
                      class="pl-10 pr-4 py-2 bg-black/60 border border-amber-700/30 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-amber-100 w-64"
                    />
                    <svg class="w-5 h-5 text-amber-300/60 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                    </svg>
                  </div>
                </div>
                
                <!-- Tableau des utilisateurs -->
                <div v-if="loading.users" class="flex justify-center items-center py-8">
                  <div class="text-amber-300">Chargement des utilisateurs...</div>
                </div>
                <div v-else class="overflow-x-auto">
                  <table class="min-w-full divide-y divide-amber-800/30 border-collapse">
                    <thead>
                      <tr class="bg-gradient-to-r from-amber-950/70 via-black/80 to-amber-950/70">
                        <th class="px-6 py-3 text-left text-xs font-semibold text-amber-300 uppercase tracking-wider">Utilisateur</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-amber-300 uppercase tracking-wider">Email</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-amber-300 uppercase tracking-wider">Rôles</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-amber-300 uppercase tracking-wider">Matchs</th>
                        <th class="px-6 py-3 text-center text-xs font-semibold text-amber-300 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody class="bg-black/30 divide-y divide-amber-800/20">
                      <tr v-for="(user, index) in filteredUsers" :key="user.id" :class="index % 2 === 0 ? 'bg-black/40' : 'bg-amber-950/20'">
                        <td class="px-6 py-4 whitespace-nowrap">
                          <div class="flex items-center">
                            <div class="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-black to-amber-900/40 flex items-center justify-center border border-amber-700/40">
                              <span class="text-amber-300 text-lg font-bold">{{ getInitials(user.username) }}</span>
                            </div>
                            <div class="ml-4">
                              <div class="text-sm font-medium text-amber-200">{{ user.username }}</div>
                              <div class="text-xs text-amber-200/60">ID: {{ user.id }}</div>
                            </div>
                          </div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                          <div class="text-sm text-amber-200">{{ user.email }}</div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                          <span v-for="role in user.roles" :key="role" 
                            class="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full mr-1"
                            :class="role.name === 'ROLE_ADMIN' ? 'bg-amber-900/40 text-amber-300' : 'bg-gray-800 text-gray-300'"
                          >
                            {{ role.name ? role.name.replace('ROLE_', '') : role.replace('ROLE_', '') }}
                          </span>
                          <span v-if="!user.roles || user.roles.length === 0" class="text-amber-300/50 text-xs">
                            Aucun rôle
                          </span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-amber-200">
                          {{ user.matchCount || 0 }}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div class="flex justify-center space-x-3">
                            <button @click="editUser(user)" class="text-amber-400 hover:text-amber-300" title="Modifier">
                              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
                              </svg>
                            </button>
                            <button @click="deleteUser(user)" class="text-red-400 hover:text-red-300" title="Supprimer">
                              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4a2 2 0 100 4 2 2 0 000-4zm0 0c1.105 0 2.012.895 2.012 2s-.897 2-2.012 2c-1.104 0-2.011-.895-2.011-2s.907-2 2.011-2z"/>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                      <tr v-if="filteredUsers.length === 0">
                        <td colspan="5" class="px-6 py-4 text-center text-amber-300/60">
                          Aucun utilisateur trouvé
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Modération de matchs -->
          <div v-if="activeTab === 'matches'" class="animate-fadeIn">
            <div class="mb-8 bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/40 p-6 relative box-glow">
              <div class="absolute inset-0 opacity-5 bg-amber-300 pattern-warhammer"></div>
              <div class="relative">
                <div class="flex justify-between items-center mb-6">
                  <h2 class="text-xl font-bold text-amber-300 flex items-center">
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                    </svg>
                    Gestion des matchs
                  </h2>
                  <div class="flex space-x-3">
                    <div class="relative">
                      <input 
                        type="text" 
                        v-model="searchMatch" 
                        placeholder="Rechercher un match..." 
                        class="pl-10 pr-4 py-2 bg-black/60 border border-amber-700/30 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-amber-100 w-64"
                      />
                      <svg class="w-5 h-5 text-amber-300/60 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                      </svg>
                    </div>
                    <button 
                      @click="refreshMatches"
                      class="px-4 py-2 bg-gradient-to-r from-amber-600/80 to-amber-800/80 text-amber-100 rounded-lg hover:from-amber-600/90 hover:to-amber-800/90 transition-all duration-300 border border-amber-700/40 flex items-center"
                    >
                      <svg class="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                      </svg>
                      Actualiser
                    </button>
                  </div>
                </div>
                
                <!-- Tableau des matchs -->
                <div v-if="loading.matches" class="flex justify-center items-center py-8">
                  <div class="text-amber-300">Chargement des matchs...</div>
                </div>
                <div v-else class="overflow-x-auto">
                  <table class="min-w-full divide-y divide-amber-800/30 border-collapse">
                    <thead>
                      <tr class="bg-gradient-to-r from-amber-950/70 via-black/80 to-amber-950/70">
                        <th class="px-6 py-3 text-left text-xs font-semibold text-amber-300 uppercase tracking-wider">Date</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-amber-300 uppercase tracking-wider">Joueur 1</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-amber-300 uppercase tracking-wider">Joueur 2</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-amber-300 uppercase tracking-wider">Score</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-amber-300 uppercase tracking-wider">Armées</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-amber-300 uppercase tracking-wider">Type</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-amber-300 uppercase tracking-wider">Statut</th>
                        <th class="px-6 py-3 text-center text-xs font-semibold text-amber-300 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody class="bg-black/30 divide-y divide-amber-800/20">
                      <tr v-for="(match, index) in filteredMatches" :key="match.id" :class="index % 2 === 0 ? 'bg-black/40' : 'bg-amber-950/20'">
                        <td class="px-6 py-4 whitespace-nowrap">
                          <div class="text-sm text-amber-200">{{ formatDate(match.date) }}</div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                          <div class="flex items-center">
                            <div class="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-black to-amber-900/40 flex items-center justify-center border border-amber-700/40">
                              <span class="text-amber-300 text-lg font-bold">{{ getInitials(match.player1) }}</span>
                            </div>
                            <div class="ml-4">
                              <div class="text-sm font-medium text-amber-200">{{ match.player1 }}</div>
                            </div>
                          </div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                          <div class="flex items-center">
                            <div class="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-black to-amber-900/40 flex items-center justify-center border border-amber-700/40">
                              <span class="text-amber-300 text-lg font-bold">{{ getInitials(match.player2) }}</span>
                            </div>
                            <div class="ml-4">
                              <div class="text-sm font-medium text-amber-200">{{ match.player2 }}</div>
                            </div>
                          </div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                          <div class="text-sm text-amber-200">{{ match.score }}</div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                          <div class="text-sm text-amber-200">{{ match.armee1 }} vs {{ match.armee2 }}</div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                          <span class="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full"
                            :class="getMatchTypeStyle(match.matchType)"
                          >
                            {{ getMatchTypeLabel(match.matchType) }}
                          </span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                          <span class="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full"
                            :class="match.statut === 'VALIDATED' ? 'bg-green-900/40 text-green-300' : 'bg-yellow-900/40 text-yellow-300'"
                          >
                            {{ match.statut === 'VALIDATED' ? 'Validé' : 'En attente' }}
                          </span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div class="flex justify-center space-x-3">
                            <button @click="editMatch(match)" class="text-amber-400 hover:text-amber-300" title="Modifier">
                              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
                              </svg>
                            </button>
                            <button @click="deleteMatch(match)" class="text-red-400 hover:text-red-300" title="Supprimer">
                              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4a2 2 0 100 4 2 2 0 000-4zm0 0c1.105 0 2.012.895 2.012 2s-.897 2-2.012 2c-1.104 0-2.011-.895-2.011-2s.907-2 2.011-2z"/>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                      <tr v-if="filteredMatches.length === 0">
                        <td colspan="8" class="px-6 py-4 text-center text-amber-300/60">
                          Aucun match trouvé
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Modal d'édition d'utilisateur -->
    <div v-if="showUserModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div class="bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl border-2 border-amber-700/40 p-6 w-full max-w-md relative box-glow">
        <div class="absolute inset-0 opacity-5 bg-amber-300 pattern-warhammer"></div>
        <div class="relative">
          <h3 class="text-xl font-bold text-amber-300 mb-4">Modifier l'utilisateur</h3>
          
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-amber-200 mb-2">Nom d'utilisateur</label>
              <input 
                v-model="editingUser.username"
                type="text" 
                class="w-full px-3 py-2 bg-black/60 border border-amber-700/30 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-amber-100"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-amber-200 mb-2">Email</label>
              <input 
                v-model="editingUser.email"
                type="email" 
                class="w-full px-3 py-2 bg-black/60 border border-amber-700/30 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-amber-100"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-amber-200 mb-2">Rôles</label>
              <div class="text-sm text-amber-300/70">
                <span v-for="role in editingUser.roles" :key="role" 
                  class="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full mr-1"
                  :class="role.name === 'ROLE_ADMIN' ? 'bg-amber-900/40 text-amber-300' : 'bg-gray-800 text-gray-300'"
                >
                  {{ role.name ? role.name.replace('ROLE_', '') : role.replace('ROLE_', '') }}
                </span>
                <span v-if="!editingUser.roles || editingUser.roles.length === 0" class="text-amber-300/50">
                  Aucun rôle
                </span>
              </div>
              <p class="text-xs text-amber-300/50 mt-1">Les rôles ne peuvent pas être modifiés via cette interface</p>
            </div>
          </div>
          
          <div class="flex justify-end space-x-3 mt-6">
            <button 
              @click="showUserModal = false"
              class="px-4 py-2 bg-gray-600/80 text-gray-200 rounded-lg hover:bg-gray-600/90 transition-all duration-300"
            >
              Annuler
            </button>
            <button 
              @click="saveUser"
              class="px-4 py-2 bg-gradient-to-r from-amber-600/80 to-amber-800/80 text-amber-100 rounded-lg hover:from-amber-600/90 hover:to-amber-800/90 transition-all duration-300 border border-amber-700/40"
            >
              Sauvegarder
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal d'édition de match -->
    <div v-if="showMatchModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div class="bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl border-2 border-amber-700/40 p-6 w-full max-w-lg relative box-glow">
        <div class="absolute inset-0 opacity-5 bg-amber-300 pattern-warhammer"></div>
        <div class="relative">
          <h3 class="text-xl font-bold text-amber-300 mb-4">Modifier le match</h3>
          
          <div class="space-y-4">
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-amber-200 mb-2">Joueur 1</label>
                <input 
                  v-model="editingMatch.player1"
                  type="text" 
                  class="w-full px-3 py-2 bg-black/60 border border-amber-700/30 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-amber-100"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-amber-200 mb-2">Joueur 2</label>
                <input 
                  v-model="editingMatch.player2"
                  type="text" 
                  class="w-full px-3 py-2 bg-black/60 border border-amber-700/30 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-amber-100"
                />
              </div>
            </div>
            
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-amber-200 mb-2">Score Joueur 1</label>
                <input 
                  v-model.number="editingMatch.scoreUser1"
                  type="number" 
                  class="w-full px-3 py-2 bg-black/60 border border-amber-700/30 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-amber-100"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-amber-200 mb-2">Score Joueur 2</label>
                <input 
                  v-model.number="editingMatch.scoreUser2"
                  type="number" 
                  class="w-full px-3 py-2 bg-black/60 border border-amber-700/30 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-amber-100"
                />
              </div>
            </div>
            
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-amber-200 mb-2">Armée 1</label>
                <input 
                  v-model="editingMatch.armee1"
                  type="text" 
                  class="w-full px-3 py-2 bg-black/60 border border-amber-700/30 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-amber-100"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-amber-200 mb-2">Armée 2</label>
                <input 
                  v-model="editingMatch.armee2"
                  type="text" 
                  class="w-full px-3 py-2 bg-black/60 border border-amber-700/30 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-amber-100"
                />
              </div>
            </div>
            
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-amber-200 mb-2">Date</label>
                <input 
                  v-model="editingMatch.date"
                  type="date" 
                  class="w-full px-3 py-2 bg-black/60 border border-amber-700/30 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-amber-100"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-amber-200 mb-2">Type de match</label>
                <select 
                  v-model="editingMatch.matchType"
                  class="w-full px-3 py-2 bg-black/60 border border-amber-700/30 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-amber-100"
                >
                  <option value="LADDER">Ladder</option>
                  <option value="LEAGUE">League</option>
                </select>
              </div>
            </div>
          </div>
          
          <div class="flex justify-end space-x-3 mt-6">
            <button 
              @click="showMatchModal = false"
              class="px-4 py-2 bg-gray-600/80 text-gray-200 rounded-lg hover:bg-gray-600/90 transition-all duration-300"
            >
              Annuler
            </button>
            <button 
              @click="saveMatch"
              class="px-4 py-2 bg-gradient-to-r from-amber-600/80 to-amber-800/80 text-amber-100 rounded-lg hover:from-amber-600/90 hover:to-amber-800/90 transition-all duration-300 border border-amber-700/40"
            >
              Sauvegarder
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de confirmation de suppression -->
    <div v-if="showConfirmModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div class="bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl border-2 border-amber-700/40 p-6 w-full max-w-md relative box-glow">
        <div class="absolute inset-0 opacity-5 bg-amber-300 pattern-warhammer"></div>
        <div class="relative">
          <h3 class="text-xl font-bold text-amber-300 mb-4">Confirmation de suppression</h3>
          
          <p class="text-amber-200 mb-6">{{ confirmMessage }}</p>
          
          <div class="flex justify-end space-x-3">
            <button 
              @click="showConfirmModal = false"
              class="px-4 py-2 bg-gray-600/80 text-gray-200 rounded-lg hover:bg-gray-600/90 transition-all duration-300"
            >
              Annuler
            </button>
            <button 
              @click="confirmDelete"
              class="px-4 py-2 bg-gradient-to-r from-red-600/80 to-red-800/80 text-red-100 rounded-lg hover:from-red-600/90 hover:to-red-800/90 transition-all duration-300 border border-red-700/40"
            >
              Supprimer
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue';
import AdminService from '../services/admin.service';

export default {
  name: 'AdminView',
  setup() {
    const activeTab = ref('dashboard');
    const searchUser = ref('');
    const searchMatch = ref('');
    const recentActivities = ref([]);
    const activitiesFilter = ref('all'); // all | match | user | moderation
    const activitiesPage = ref(1);
    const ACTIVITIES_PAGE_SIZE = 10;
    const loadingActivities = ref(false);
    const showUserModal = ref(false);
    const showMatchModal = ref(false);
    const showConfirmModal = ref(false);
    const editingUser = ref({
      id: null,
      username: '',
      email: '',
      roles: [],
      active: true
    });
    const editingMatch = ref({
      id: null,
      player1: '',
      player2: '',
      scoreUser1: 0,
      scoreUser2: 0,
      armee1: '',
      armee2: '',
      date: new Date().toISOString().split('T')[0],
      matchType: 'LADDER'
    });
    const confirmMessage = ref('');
    const pendingDeleteId = ref(null);
    const pendingDeleteType = ref('');
    const loading = ref({
      users: false,
      matches: false,
      stats: false,
      global: false
    });
    const stats = ref({
      users: 0,
      newUsers: 0,
      matches: 0,
      newMatches: 0,
      activePlayers: 0,
      monthlyMatches: 0
    });
    
    // Données réelles
    const users = ref([]);
    const matches = ref([]);
    
    const activityTypeClasses = {
      user: 'bg-gradient-to-br from-blue-600/80 to-blue-800/80 text-white border border-blue-700/50',
      match: 'bg-gradient-to-br from-green-600/80 to-green-800/80 text-white border border-green-700/50',
      moderation: 'bg-gradient-to-br from-amber-600/80 to-amber-800/80 text-white border border-amber-700/50'
    };
    
    const activityTypeIcons = {
      user: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
      match: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
      moderation: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
    };
    
    const tabs = [
      { id: 'dashboard', name: 'Tableau de bord', icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6' },
      { id: 'users', name: 'Gestion des utilisateurs', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
      { id: 'matches', name: 'Gestion des matchs', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
    ];
    const filteredActivities = computed(() => {
      if (activitiesFilter.value === 'all') return recentActivities.value;
      return recentActivities.value.filter(a => a.type === activitiesFilter.value);
    });

    const totalActivitiesPages = computed(() => {
      return Math.max(1, Math.ceil(filteredActivities.value.length / ACTIVITIES_PAGE_SIZE));
    });

    const pagedActivities = computed(() => {
      const start = (activitiesPage.value - 1) * ACTIVITIES_PAGE_SIZE;
      return filteredActivities.value.slice(start, start + ACTIVITIES_PAGE_SIZE);
    });

    const loadActivities = async () => {
      loadingActivities.value = true;
      try {
        recentActivities.value = await AdminService.getRecentActivities();
        activitiesPage.value = 1; // reset page on reload
      } catch (e) {
        console.error('Erreur lors du chargement des activités:', e);
      } finally {
        loadingActivities.value = false;
      }
    };
    
    // Computed properties pour le filtrage
    const filteredUsers = computed(() => {
      if (!searchUser.value) return users.value;
      return users.value.filter(user => 
        user.username.toLowerCase().includes(searchUser.value.toLowerCase()) ||
        user.email.toLowerCase().includes(searchUser.value.toLowerCase())
      );
    });
    
    const filteredMatches = computed(() => {
      if (!searchMatch.value) return matches.value;
      return matches.value.filter(match => 
        match.player1.toLowerCase().includes(searchMatch.value.toLowerCase()) ||
        match.player2.toLowerCase().includes(searchMatch.value.toLowerCase()) ||
        match.armee1.toLowerCase().includes(searchMatch.value.toLowerCase()) ||
        match.armee2.toLowerCase().includes(searchMatch.value.toLowerCase())
      );
    });
    
    // Charger les données avec protection contre les rechargements multiples
    const loadStats = async () => {
      if (loading.value.stats) return; // Éviter les requêtes multiples
      loading.value.stats = true;
      try {
        stats.value = await AdminService.getStats();
      } catch (error) {
        console.error('Erreur lors du chargement des statistiques:', error);
      } finally {
        loading.value.stats = false;
      }
    };
    
    const loadUsers = async () => {
      if (loading.value.users) return; // Éviter les requêtes multiples
      loading.value.users = true;
      try {
        users.value = await AdminService.getAllUsers();
      } catch (error) {
        console.error('Erreur lors du chargement des utilisateurs:', error);
      } finally {
        loading.value.users = false;
      }
    };
    
    const loadMatches = async () => {
      if (loading.value.matches) return; // Éviter les requêtes multiples
      loading.value.matches = true;
      try {
        matches.value = await AdminService.getAllMatches();
      } catch (error) {
        console.error('Erreur lors du chargement des matchs:', error);
      } finally {
        loading.value.matches = false;
      }
    };
    
    // Actions sur les utilisateurs
    const editUser = (user) => {
      editingUser.value = { ...user };
      showUserModal.value = true;
    };
    
    
    const deleteUser = (user) => {
      confirmMessage.value = `Êtes-vous sûr de vouloir supprimer l'utilisateur "${user.username}" ?`;
      pendingDeleteId.value = user.id;
      pendingDeleteType.value = 'user';
      showConfirmModal.value = true;
    };
    
    // Actions sur les matchs
    const editMatch = (match) => {
      editingMatch.value = { ...match };
      showMatchModal.value = true;
    };
    
    const deleteMatch = (match) => {
      confirmMessage.value = `Êtes-vous sûr de vouloir supprimer ce match ?`;
      pendingDeleteId.value = match.id;
      pendingDeleteType.value = 'match';
      showConfirmModal.value = true;
    };
    
    const refreshMatches = async () => {
      await loadMatches();
    };
    
    
    // Sauvegarder les modifications
    const saveUser = async () => {
      try {
        await AdminService.updateUser(editingUser.value.id, editingUser.value);
        await loadUsers();
        showUserModal.value = false;
        alert('Utilisateur modifié avec succès !');
      } catch (error) {
        console.error('Erreur lors de la sauvegarde de l\'utilisateur:', error);
        alert('Erreur lors de la modification de l\'utilisateur. Vérifiez la console pour plus de détails.');
      }
    };
    
    const saveMatch = async () => {
      try {
        await AdminService.updateMatch(editingMatch.value.id, editingMatch.value);
        await loadMatches();
        showMatchModal.value = false;
      } catch (error) {
        console.error('Erreur lors de la sauvegarde du match:', error);
      }
    };
    
    const confirmDelete = async () => {
      try {
        if (pendingDeleteType.value === 'user') {
          await AdminService.deleteUser(pendingDeleteId.value);
          await loadUsers();
          alert('Utilisateur supprimé avec succès !');
        } else if (pendingDeleteType.value === 'match') {
          await AdminService.deleteMatch(pendingDeleteId.value);
          await loadMatches();
          alert('Match supprimé avec succès !');
        }
        showConfirmModal.value = false;
        pendingDeleteId.value = null;
        pendingDeleteType.value = '';
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        alert('Erreur lors de la suppression. Vérifiez la console pour plus de détails.');
      }
    };
    
    // Formatage de date
    const formatDate = (timestamp) => {
      if (!timestamp) return 'Date inconnue';
      const date = new Date(timestamp);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    };
    
    // Obtenir les initiales d'un nom d'utilisateur
    const getInitials = (name) => {
      if (!name) return '';
      return name.split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase();
    };
    
    // Fonctions pour gérer l'affichage du type de match
    const getMatchTypeLabel = (matchType) => {
      const labels = {
        'LEAGUE': 'League',
        'LADDER': 'Ladder'
      };
      return labels[matchType] || 'Inconnu';
    };

    const getMatchTypeStyle = (matchType) => {
      const styles = {
        'LEAGUE': 'bg-blue-900/60 text-blue-200 border-blue-500/80',
        'LADDER': 'bg-purple-900/60 text-purple-200 border-purple-500/80'
      };
      return styles[matchType] || 'bg-gray-900/60 text-gray-200 border-gray-500/80';
    };
    
    // Debounce pour les recherches
    let searchTimeout = null;
    
    const debouncedSearch = (callback, delay = 300) => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      searchTimeout = setTimeout(callback, delay);
    };
    
    // Watchers pour les recherches avec debounce
    watch(searchUser, () => {
      debouncedSearch(() => {
        // Le filtrage se fait automatiquement via computed properties
        // Pas besoin de recharger les données
      });
    });
    
    watch(searchMatch, () => {
      debouncedSearch(() => {
        // Le filtrage se fait automatiquement via computed properties
        // Pas besoin de recharger les données
      });
    });

    // Charger les données au montage du composant
    onMounted(async () => {
      loading.value.global = true;
      try {
        await Promise.all([
          loadStats(),
          loadUsers(),
          loadMatches()
        ]);
        
        // Charger les activités récentes
        await loadActivities();
      } catch (error) {
        console.error('Erreur lors du chargement initial des données:', error);
      } finally {
        loading.value.global = false;
      }
    });
    
    return {
      activeTab,
      searchUser,
      searchMatch,
      filteredUsers,
      filteredMatches,
      stats,
      recentActivities,
      activitiesFilter,
      loadingActivities,
      filteredActivities,
      loadActivities,
      activityTypeClasses,
      activityTypeIcons,
      tabs,
      showUserModal,
      showMatchModal,
      showConfirmModal,
      editingUser,
      editingMatch,
      confirmMessage,
      loading,
      activitiesPage,
      totalActivitiesPages,
      pagedActivities,
      
      // Méthodes
      editUser,
      saveUser,
      deleteUser,
      editMatch,
      saveMatch,
      deleteMatch,
      confirmDelete,
      refreshMatches,
      formatDate,
      getInitials,
      getMatchTypeLabel,
      getMatchTypeStyle
    };
  }
};
</script>

<style scoped>
.admin {
  background-image: 
    radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
    radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
  background-size: 40px 40px;
}

.pattern-warhammer {
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23f59e0b' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.animate-fadeIn {
  animation: fadeIn 0.3s ease-in-out forwards;
}

@keyframes fadeIn {
  0% { opacity: 0; }
  100% { opacity: 1; }
}

.box-glow {
  box-shadow: 0 0 15px rgba(217, 119, 6, 0.2);
}
</style> 